import * as React from 'react';

import { Header, Button, Table, Breadcrumb } from 'semantic-ui-react';
import Lesson from '../../../modules/lessons/domain/aggregates/lesson/Lesson';
import * as XmlJS from 'xml-js';
import MonoComponent from '../shared/MonoComponent';
import MonoLineComponent from '../shared/MonoLineComponent';
import SetComponent from '../shared/SetComponent';
import Page from '../../Page';
import MathComponent from '../shared/MathComponent';
import {Link} from 'react-router-dom';
import MathBoxComponent from '../shared/MathBoxComponent';
import Vectors1Component from './WebGL/Vectors1Component';
import Vectors2Component from './WebGL/Vectors2Component';
import Vectors3Component from './WebGL/Vectors3Component';
import CirclesAndAngles1Component from './WebGL/CirclesAndAngles1Component';
import LinesComponent from './WebGL/LinesComponent';
import CurvesComponent from './WebGL/CurvesComponent';
import AnglesComponent from './WebGL/AnglesComponent';
import Angles2Component from './WebGL/Angles2Component';
import Angles3Component from './WebGL/Angles3Component';
import Course from '../../../modules/lessons/domain/aggregates/course/Course';

interface Props {
	lesson: Lesson;
	course?: Course;
	noButtons?: boolean;
}

interface State {
	expanded: boolean[];
}

export default class LessonComponent extends React.Component<Props, State> {

	private expandableIndex = 0;

	public constructor () {
		super();

		this.state = { expanded: [] };
	}

	componentDidMount () {
		Page.requestTypesetting();
	}

	componentDidUpdate () {
		Page.requestTypesetting();
	}

	renderContent = (content: string) => {
		try {
			this.expandableIndex = 0;
			return this.parse(XmlJS.xml2js('<root>' + content + '</root>').elements[0]);

		} catch (error) {
			return error.message;
		}
	};

	render () {
		const lesson = this.props.lesson;
		const course = this.props.course;

		let content = lesson.getContent();

		if (course instanceof Course && course.getId().getValue() === 9) {
			content += '<subtext>This course is based on the <a href="https://openstax.org/details/books/biology">free textbook on Biology</a> by OpenStax.</subtext>';
		}

		return (
			<div className='lesson'>
				{this.renderContent(content)}
			</div>
		);
	}

	expand = (index: number) => {
		const expanded = this.state.expanded;
		expanded[index] = true;
		this.setState({expanded});
	}

	private parse (value: any, key?: number): any {
		const state = this.state;

		if (Array.isArray(value)) {
			return value.length === 1 ? this.parse(value[0]) : value.map((elementValue, elementKey) => this.parse(elementValue, elementKey));
		}

		if (typeof value === 'object' && value.type === 'text') {
			return <span key={key}>{value.text}</span>;
		}

		if (typeof value === 'object' && value.type === 'element') {
			if (value.name === 'root') {
				return <div key={key}>{this.parse(value.elements)}</div>;
			}

			if (value.name === 'p') {
				return <p key={key}>{this.parse(value.elements)}</p>;
			}

			if (value.name === 'strong') {
				return <strong key={key}>{this.parse(value.elements)}</strong>;
			}

			if (value.name === 'em') {
				return <em key={key}>{this.parse(value.elements)}</em>;
			}

			if (value.name === 'Set') {
				return <SetComponent key={key}>{this.parse(value.elements)}</SetComponent>;
			}

			if (value.name === 'Mono') {
				return <MonoComponent key={key}>{this.parse(value.elements)}</MonoComponent>;
			}

			if (value.name === 'MonoLine') {
				return <MonoLineComponent key={key}>{this.parse(value.elements)}</MonoLineComponent>;
			}

			if (value.name === 'ul') {
				return <ul key={key}>{this.parse(value.elements)}</ul>;
			}

			if (value.name === 'ol') {
				return <ol key={key} type={value.attributes && value.attributes.type ? value.attributes.type : undefined}>{this.parse(value.elements)}</ol>;
			}

			if (value.name === 'li') {
				return <li key={key}>{this.parse(value.elements)}</li>;
			}

			if (value.name === 'footnote') {
				return <p key={key} className='footnote'>{this.parse(value.elements)}</p>;
			}

			if (value.name === 'sub') {
				return <sub key={key}>{this.parse(value.elements)}</sub>;
			}

			if (value.name === 'sup') {
				return <sup key={key}>{this.parse(value.elements)}</sup>;
			}

			if (value.name === 'a') {
				return value.attributes.href[0] !== '/' ? <a key={key} href={value.attributes.href} target='_blank'>{this.parse(value.elements)}</a> : <Link to={value.attributes.href}>{this.parse(value.elements)}</Link>;
			}

			if (value.name === 'expandable') {
				const expandableIndex = this.expandableIndex++;

				if (state.expanded[expandableIndex] !== true) {
					return <div key={key}><span style={{cursor: 'pointer', color: '#4183C4'}} onClick={() => this.expand(expandableIndex)}>{value.attributes.label}</span></div>;
				} else {
					return <div key={key}>{this.parse(value.elements)}</div>;
				}
			}

			if (value.name === 'img') {
				let cutTop = 0, cutRight = 0, cutBottom = 0, cutLeft = 0;

				if (value.attributes.crop) {
					const cuts = (value.attributes.crop as string).split(' ').map(crop => Number(crop));
					if (cuts.length === 1) {
						cutTop = cutRight = cutBottom = cutLeft = cuts[0];
					} else if (cuts.length === 2) {
						cutTop = cutBottom = cuts[0];
						cutRight = cutLeft = cuts[1];
					} else if (cuts.length === 2) {
						cutTop = cuts[0];
						cutRight = cutLeft = cuts[1];
						cutBottom = cuts[2];
					} else if (cuts.length === 4) {
						cutTop = cuts[0];
						cutRight = cuts[1];
						cutBottom = cuts[2];
						cutLeft = cuts[3];
					}
				}

				let queryArray = [];
				if (cutTop !== 0) {
					queryArray.push('top=' + cutTop);
				}
				if (cutRight !== 0) {
					queryArray.push('right=' + cutRight);
				}
				if (cutBottom !== 0) {
					queryArray.push('bottom=' + cutBottom);
				}
				if (cutLeft !== 0) {
					queryArray.push('left=' + cutLeft);
				}

				let source = value.attributes.src;

				if (queryArray.length !== 0) {
					source = 'https://brainbrooder.com/api/image?url=' + encodeURIComponent(source) + '&' + queryArray.join('&');
				}

				return <img key={key} alt={value.attributes.alt} title={value.attributes.title} src={source} style={{display: 'block', maxWidth: '100%', margin: '1em auto', backgroundColor: value.attributes.background || 'inherit', filter: 'invert(' + (value.attributes.inverted === 'true' ? .888 : 0) + ')'}} />;
			}

			if (value.name === 'caption') {
				return <span key={key} style={{display: 'block', fontSize: '0.7em', textAlign: 'center', marginTop: '-1em', color: '#cccccc'}}>{this.parse(value.elements)}</span>;
			}

			if (value.name === 'subtext') {
				return <p key={key} style={{fontSize: '0.7em', textAlign: 'center', color: '#cccccc'}}>{this.parse(value.elements)}</p>;
			}

			if (value.name === 'section') {
				return <p key={key} style={{fontSize: '1.3em', marginBottom: '0.3em'}}>{this.parse(value.elements)}</p>;
			}

			if (value.name === 'subsection') {
				return <p key={key} style={{fontWeight: 'bold', marginBottom: '0.1em'}}>{this.parse(value.elements)}</p>;
			}

			if (value.name === 'br') {
				return <br key={key}/>;
			}

			if (value.name === 'hr') {
				return <hr key={key}/>;
			}

			if (value.name === 'Math') {
				return <MathComponent key={key} value={value.elements[0].text} />;
			}

			if (value.name === 'Box') {
				return <div style={{width: '100%', fontSize: '0.9em', padding: '24px', backgroundColor: '#333333', margin: '0 auto 21px auto'}}>{this.parse(value.elements)}</div>;
			}

			if (value.name === 'MathBox') {
				return <MathBoxComponent key={key} value={value.elements[0].text} />;
			}

			if (value.name === 'table') {
				return <Table key={key} inverted celled style={{textAlign: 'center'}}>{this.parse(value.elements)}</Table>;
			}

			if (value.name === 'thead') {
				return <Table.Header key={key}>{this.parse(value.elements)}</Table.Header>;
			}

			if (value.name === 'tbody') {
				return <Table.Body key={key}>{this.parse(value.elements)}</Table.Body>;
			}

			if (value.name === 'tfoot') {
				return <Table.Footer key={key}>{this.parse(value.elements)}</Table.Footer>;
			}

			if (value.name === 'tr') {
				return <Table.Row key={key}>{this.parse(value.elements)}</Table.Row>;
			}

			if (value.name === 'th') {
				return <Table.HeaderCell key={key} style={{borderColor: 'grey'}} colSpan={value.attributes && value.attributes.colspan || 1}>{this.parse(value.elements)}</Table.HeaderCell>;
			}

			if (value.name === 'td') {
				return <Table.Cell key={key} style={{borderColor: 'grey'}} colSpan={value.attributes && value.attributes.colspan || 1}>{this.parse(value.elements)}</Table.Cell>;
			}

			if (value.name === 'norender') {
				return '';
			}

			if (value.name === 'large') {
				return <span style={{fontSize: '1.5em'}}>{this.parse(value.elements)}</span>;
			}

			if (value.name === 'largecontainer') {
				return <div style={{fontSize: '1.5em'}}>{this.parse(value.elements)}</div>;
			}

			if (value.name === 'Vectors1GL') { return <Vectors1Component key={key} width={value.attributes.width} height={value.attributes.height} />; }
			if (value.name === 'Vectors2GL') { return <Vectors2Component key={key} width={value.attributes.width} height={value.attributes.height} />; }
			if (value.name === 'Vectors3GL') { return <Vectors3Component key={key} width={value.attributes.width} height={value.attributes.height} />; }
			if (value.name === 'Circles1GL') { return <CirclesAndAngles1Component key={key} width={value.attributes.width} height={value.attributes.height} />; }
			if (value.name === 'LinesGL') { return <LinesComponent key={key} width={value.attributes.width} height={value.attributes.height} />; }
			if (value.name === 'CurvesGL') { return <CurvesComponent key={key} width={value.attributes.width} height={value.attributes.height} />; }
			if (value.name === 'AnglesGL') { return <AnglesComponent key={key} width={value.attributes.width} height={value.attributes.height} />; }
			if (value.name === 'Angles2GL') { return <Angles2Component key={key} width={value.attributes.width} height={value.attributes.height} />; }
			if (value.name === 'Angles3GL') { return <Angles3Component key={key} width={value.attributes.width} height={value.attributes.height} />; }

			return <div key={key} style={{color: 'red'}}>Unknown element <strong>{value.name}</strong> encountered while parsing LessonComponent.</div>;
		}

		if (value === undefined) {
			return null;
			// return <div key={key} style={{color: 'red'}}>Element is empty.</div>;
		}

		console.log('Unknown choice', value);
		return <div key={key} style={{color: 'red'}}>Unknown value <strong>{value}</strong> encountered while parsing LessonComponent.</div>;
	}
}


// WEBPACK FOOTER //
// ./code/project/pages/components/lesson/LessonComponent.tsx